import * as React from "react";
import { ChevronUp, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "./button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./dropdown-menu";
import { cn } from "@/lib/utils";

// Generic types for the table
export type SortOrder = "asc" | "desc" | null;

export interface ColumnDef<T> {
  /** Unique identifier for the column */
  id: string;
  /** Header label to display */
  header: string;
  /** Function to extract the cell value from a row */
  accessor: (row: T) => any;
  /** Optional custom render function for the cell */
  render?: (value: any, row: T) => React.ReactNode;
  /** Whether the column is sortable */
  sortable?: boolean;
  /** Optional CSS classes for the header */
  headerClassName?: string;
  /** Optional CSS classes for the cell */
  cellClassName?: string;
  /** Optional width for the column */
  width?: string;
}

export interface PaginationInfo {
  page: number;
  page_size: number;
  total_items: number;
  total_pages: number;
  has_next: boolean;
  has_previous: boolean;
}

export interface DataTableProps<T> {
  /** Array of data rows */
  data: T[];
  /** Column definitions */
  columns: ColumnDef<T>[];
  /** Pagination information */
  pagination?: PaginationInfo;
  /** Current sort key */
  sortKey?: string;
  /** Current sort order */
  sortOrder?: SortOrder;
  /** Callback when sort changes */
  onSortChange?: (key: string, order: SortOrder) => void;
  /** Callback when page changes */
  onPageChange?: (page: number) => void;
  /** Callback when page size changes */
  onPageSizeChange?: (pageSize: number) => void;
  /** Loading state */
  isLoading?: boolean;
  /** Empty state message */
  emptyMessage?: string;
  /** Optional CSS classes for the table */
  className?: string;
  /** Row key extractor function */
  getRowKey: (row: T) => string | number;
  /** Optional: Enable expand/collapse functionality */
  enableExpand?: boolean;
  /** Optional: Render function for expanded row content */
  renderExpandedContent?: (row: T) => React.ReactNode;
  /** Optional: External expanded rows state (if managing state externally) */
  expandedRows?: Set<string | number>;
  /** Optional: Callback when row expansion changes (if managing state externally) */
  onToggleRow?: (rowKey: string | number) => void;
  /** Optional: Show expand column (defaults to true when enableExpand is true) */
  showExpandColumn?: boolean;
}

/**
 * Generic DataTable Component
 * 
 * A reusable table component that supports:
 * - Custom column definitions
 * - Sorting (optional per column)
 * - Pagination
 * - Loading states
 * - Empty states
 * - Custom cell rendering
 * 
 * @example
 * ```tsx
 * const columns: ColumnDef<User>[] = [
 *   {
 *     id: "name",
 *     header: "Name",
 *     accessor: (row) => row.name,
 *     sortable: true,
 *   },
 *   {
 *     id: "email",
 *     header: "Email",
 *     accessor: (row) => row.email,
 *     render: (value) => <a href={`mailto:${value}`}>{value}</a>,
 *   },
 * ];
 * 
 * <DataTable
 *   data={users}
 *   columns={columns}
 *   pagination={paginationInfo}
 *   onSortChange={(key, order) => handleSort(key, order)}
 *   onPageChange={(page) => handlePageChange(page)}
 *   getRowKey={(row) => row.id}
 * />
 * ```
 */
export function DataTable<T>({
  data,
  columns,
  pagination,
  sortKey,
  sortOrder,
  onSortChange,
  onPageChange,
  onPageSizeChange,
  isLoading = false,
  emptyMessage = "No data available",
  className,
  getRowKey,
  enableExpand = false,
  renderExpandedContent,
  expandedRows: externalExpandedRows,
  onToggleRow: externalToggleRow,
  showExpandColumn = true,
}: DataTableProps<T>) {
  const [internalExpandedRows, setInternalExpandedRows] = React.useState<Set<string | number>>(new Set());

  // Use external state if provided, otherwise use internal state
  const expandedRows = externalExpandedRows ?? internalExpandedRows;

  const toggleRow = (rowKey: string | number) => {
    if (externalToggleRow) {
      externalToggleRow(rowKey);
    } else {
      setInternalExpandedRows((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(rowKey)) {
          newSet.delete(rowKey);
        } else {
          newSet.add(rowKey);
        }
        return newSet;
      });
    }
  };

  // Only show expand column if enableExpand is true AND showExpandColumn is true
  const shouldShowExpandColumn = enableExpand && showExpandColumn;

  const handleSort = (columnId: string) => {
    if (!onSortChange) return;

    const column = columns.find((col) => col.id === columnId);
    if (!column?.sortable) return;

    let newOrder: SortOrder = "asc";
    if (sortKey === columnId) {
      if (sortOrder === "asc") {
        newOrder = "desc";
      } else if (sortOrder === "desc") {
        newOrder = null;
      }
    }

    onSortChange(newOrder ? columnId : "", newOrder);
  };

  const getSortIcon = (columnId: string) => {
    if (sortKey !== columnId || !sortOrder) {
      return (
        <span className="inline-flex flex-col opacity-30">
          <ChevronUp className="h-3 w-3 -mb-0.5" />
          <ChevronDown className="h-3 w-3" />
        </span>
      );
    }

    if (sortOrder === "asc") {
      return <ChevronUp className="h-4 w-4 text-primary" />;
    }

    return <ChevronDown className="h-4 w-4 text-primary" />;
  };

  const pageSizeOptions = [5, 10, 20, 50, 100];

  return (
    <div className={cn("w-full", className)}>
      {/* Table */}
      <div className="rounded-md border border-border border-[#E6E6E6] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead className="bg-[#F6F9FE]">
              <tr>
                {shouldShowExpandColumn && (
                  <th className="px-4 py-3 text-left text-sm font-medium text-foreground w-12">
                    <span className="sr-only">Expand</span>
                  </th>
                )}
                {columns.map((column) => (
                  <th
                    key={column.id}
                    className={cn(
                      "px-4 py-3 text-left text-sm font-medium",
                      column.sortable && "cursor-pointer hover:bg-muted/70 transition-colors",
                      column.headerClassName,
                      column.width && `w-[${column.width}]`,
                      "bg-[#F6F9FE]"
                    )}
                    style={column.width ? { width: column.width } : undefined}
                    onClick={() => handleSort(column.id)}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-[#005587]">{column.header}</span>
                      {column.sortable && (
                        <span className="flex-shrink-0">
                          {getSortIcon(column.id)}
                        </span>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-background">
              {isLoading ? (
                <tr>
                  <td
                    colSpan={columns.length + (shouldShowExpandColumn ? 1 : 0)}
                    className="px-4 py-8 text-center text-sm text-muted-foreground"
                  >
                    Loading...
                  </td>
                </tr>
              ) : data.length === 0 ? (
                <tr>
                  <td
                    colSpan={columns.length + (shouldShowExpandColumn ? 1 : 0)}
                    className="px-4 py-8 text-center text-sm text-muted-foreground"
                  >
                    {emptyMessage}
                  </td>
                </tr>
              ) : (
                data.map((row) => {
                  const rowKey = getRowKey(row);
                  const isExpanded = expandedRows.has(rowKey);
                  return (
                    <React.Fragment key={rowKey}>
                      <tr className="border-t border-border hover:bg-muted/30 transition-colors">
                        {shouldShowExpandColumn && (
                          <td className="px-4 py-3">
                            <button
                              onClick={() => toggleRow(rowKey)}
                              className="p-1 hover:bg-muted rounded transition-colors"
                              aria-label={isExpanded ? "Collapse row" : "Expand row"}
                            >
                              {isExpanded ? (
                                <ChevronUp className="h-4 w-4 text-muted-foreground" />
                              ) : (
                                <ChevronDown className="h-4 w-4 text-muted-foreground" />
                              )}
                            </button>
                          </td>
                        )}
                        {columns.map((column) => {
                          const value = column.accessor(row);
                          const renderedValue = column.render
                            ? column.render(value, row)
                            : value;

                          return (
                            <td
                              key={column.id}
                              className={cn(
                                "px-4 py-3 text-sm text-foreground",
                                column.cellClassName
                              )}
                            >
                              {renderedValue ?? <span className="text-muted-foreground">—</span>}
                            </td>
                          );
                        })}
                      </tr>
                      {enableExpand && isExpanded && renderExpandedContent && (
                        <tr>
                          <td
                            colSpan={columns.length + (shouldShowExpandColumn ? 1 : 0)}
                            className="p-0"
                          >
                            {renderExpandedContent(row)}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination - integrated as table footer */}
        {pagination && (
          <div className="flex items-center justify-between bg-[#f7f8fc] border-t border-[#E6E6E6] px-4 py-3">
            {/* Entry count */}
            <div className="text-sm text-muted-foreground">
              {pagination.total_items > 0
                ? `${(pagination.page - 1) * pagination.page_size + 1}-${Math.min(
                    pagination.page * pagination.page_size,
                    pagination.total_items
                  )} of ${pagination.total_items}`
                : "0 of 0"}
            </div>

            {/* Pagination controls */}
            <div className="flex items-center gap-4">
              {/* Page navigation - shown first */}
              {onPageChange && (
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onPageChange(pagination.page - 1)}
                    disabled={!pagination.has_previous}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-sm min-w-[4rem] text-center">
                    <span className="text-[#171c26]">{pagination.page}</span>
                    <span className="text-muted-foreground"> / {pagination.total_pages || 1}</span>
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onPageChange(pagination.page + 1)}
                    disabled={!pagination.has_next}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              )}

              {/* Rows per page - shown after page navigation */}
              {onPageSizeChange && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Rows per page:</span>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        className="flex items-center gap-1 text-sm text-muted-foreground hover:text-muted-foreground/80 transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                      >
                        <span>{pagination.page_size}</span>
                        <ChevronDown className="h-4 w-4 text-muted-foreground" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {pageSizeOptions.map((size) => (
                        <DropdownMenuItem
                          key={size}
                          onClick={() => onPageSizeChange(size)}
                        >
                          {size}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

